import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { RotateCcw, Download, SlidersHorizontal, X, Eye } from 'lucide-react';
import CustomDatePicker from '../../components/dashboard/CustomDatePicker';
import Pagination from '../../components/Pagination';
import { getPatientsByDate, downloadPatientsExcel } from '../../redux/reportsSlice';
import { getDoctorNameList } from '../../redux/doctorSlice';
import { getPatientById } from '../../redux/patientSlice';
import { toast } from 'react-toastify';
import CommonLoader from '../../components/CommonLoader';
import { useTranslation } from 'react-i18next';
import PatientProfileModal from '../../components/modals/PatientProfileModal';
import { getCountries } from '../../redux/masterSlice';
import { FilterPanel, FilterSection, FilterOptionList, FilterCountryList, FilterSearchableList, FilterPopover } from '../../components/common/FilterPanel';
import MasterLabel from '../../components/common/MasterLabel';
import { hospitalPatientCode } from '../../utils/patientCode';

// Debounce hook
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

const ReportsPatients = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { patientsByDate, loading, downloadLoading, totalItems } = useSelector((state) => state.reports);
    const { doctorNameList, loadingList } = useSelector((state) => state.doctor);
    const { selectedPatient, isLoading: patientLoading } = useSelector((state) => state.patient);
    const { countries } = useSelector((state) => state.master);

    const [activeTab, setActiveTab] = useState('month');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [dateErrors, setDateErrors] = useState({ startDate: '', endDate: '' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState({ gender: [], countryId: [], selectedDoctor: [] });
    const [filters, setFilters] = useState({ gender: [], countryId: [], selectedDoctor: [] });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [doctorSearch, setDoctorSearch] = useState('');
    const filterButtonRef = useRef(null);
    const filterRef = useRef(null);
    const isInitialRender = useRef(true);

    // Validate search input
    useEffect(() => {
        dispatch(getCountries({ status: 'active', limit: 100 }));
    }, [dispatch]);

    const debouncedDoctorSearch = useDebounce(doctorSearch, 400);

    // Helper function to get date range based on tab
    const getDateRange = (tab) => {
        const today = new Date();
        let from = '';
        let to = '';

        switch (tab) {
            case 'today':
                from = to = today.toISOString().split('T')[0];
                break;
            case 'week':
                const weekAgo = new Date(today);
                weekAgo.setDate(today.getDate() - 7);
                from = weekAgo.toISOString().split('T')[0];
                to = today.toISOString().split('T')[0];
                break;
            case 'month':
                const monthAgo = new Date(today);
                monthAgo.setMonth(today.getMonth() - 1);
                from = monthAgo.toISOString().split('T')[0];
                to = today.toISOString().split('T')[0];
                break;
            case 'custom':
                from = startDate;
                to = endDate;
                break;
            default:
                from = to = today.toISOString().split('T')[0];
        }

        return { from, to };
    };

    // Fetch doctor list when filter opens or search changes
    useEffect(() => {
        if (isFilterOpen) {
            dispatch(getDoctorNameList(debouncedDoctorSearch));
        }
    }, [dispatch, debouncedDoctorSearch, isFilterOpen]);

    // Sync tempFilters with filters when popup opens
    useEffect(() => {
        if (isFilterOpen) {
            setTempFilters({ ...filters });
        }
    }, [isFilterOpen]);

    // Close filter on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                filterRef.current &&
                !filterRef.current.contains(event.target) &&
                filterButtonRef.current &&
                !filterButtonRef.current.contains(event.target)
            ) {
                // Reset tempFilters to current filters when closing without applying
                setTempFilters({ ...filters });
                setIsFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [filters]);

    // Validate dates
    const validateDates = (start, end) => {
        const errors = { startDate: '', endDate: '' };
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (start) {
            const startDateObj = new Date(start);
            startDateObj.setHours(0, 0, 0, 0);

            // Check if start date is valid
            if (isNaN(startDateObj.getTime())) {
                errors.startDate = t('reportsPatients.invalid_date_format');
            }

            // Check if start date is after end date
            if (end) {
                const endDateObj = new Date(end);
                endDateObj.setHours(0, 0, 0, 0);
                if (startDateObj > endDateObj) {
                    errors.startDate = t('reportsPatients.start_after_end');
                }
            }
        }

        if (end) {
            const endDateObj = new Date(end);
            endDateObj.setHours(0, 0, 0, 0);

            // Check if end date is valid
            if (isNaN(endDateObj.getTime())) {
                errors.endDate = t('reportsPatients.invalid_date_format');
            }

            // Check if end date is in the future
            if (endDateObj > today) {
                errors.endDate = t('reportsPatients.end_in_future');
            }

            // Check if end date is before start date
            if (start) {
                const startDateObj = new Date(start);
                startDateObj.setHours(0, 0, 0, 0);
                if (endDateObj < startDateObj) {
                    errors.endDate = t('reportsPatients.end_before_start');
                }
            }
        }

        // Check if start date is required when end date is set
        if (end && !start) {
            errors.startDate = t('reportsPatients.start_required');
        }

        setDateErrors(errors);
        return Object.values(errors).every(error => !error);
    };

    // Fetch data function
    const fetchPatientsData = (page = 1) => {
        const { from, to } = getDateRange(activeTab);

        if (!from || !to) {
            if (activeTab === 'custom') {
                toast.warning(t('reportsPatients.select_both_dates'));
            }
            return;
        }

        // For custom, validate dates before fetching
        if (activeTab === 'custom') {
            if (!validateDates(startDate, endDate)) {
                return;
            }
        }

        const params = { from, to, page, limit: itemsPerPage };
        if (filters.selectedDoctor.length > 0) {
            params.doctorId = filters.selectedDoctor.map((d) => d.doctorId).join(',');
        }
        if (filters.gender.length > 0) {
            params.gender = filters.gender.join(',');
        }
        if (filters.countryId.length > 0) {
            params.countryId = filters.countryId.join(',');
        }

        console.log('fetchPatientsData - API Called - Page:', page, 'From:', from, 'To:', to, 'Limit:', itemsPerPage, 'DoctorIds:', params.doctorId || 'All', 'Genders:', params.gender || 'All', 'Countries:', params.countryId || 'All');
        dispatch(getPatientsByDate(params));
    };

    // Initial load - ONLY runs once on mount
    useEffect(() => {
        console.log('Component mounted - Initial API call');
        fetchPatientsData(1);
        // Mark that initial render is complete
        setTimeout(() => {
            isInitialRender.current = false;
        }, 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array - runs ONLY once

    // Handle tab changes (skip initial render)
    useEffect(() => {
        if (!isInitialRender.current) {
            console.log('Tab changed to:', activeTab);
            // Clear dates when switching to custom tab
            if (activeTab === 'custom') {
                setStartDate('');
                setEndDate('');
                setDateErrors({ startDate: '', endDate: '' });
            } else {
                // Fetch data automatically for non-custom tabs
                setCurrentPage(1);
                fetchPatientsData(1);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // Handle custom date changes (skip initial render)
    useEffect(() => {
        if (!isInitialRender.current && activeTab === 'custom' && startDate && endDate) {
            console.log('Custom dates changed - From:', startDate, 'To:', endDate);
            setCurrentPage(1);
            fetchPatientsData(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    // Handle filter changes (skip initial render)
    useEffect(() => {
        if (!isInitialRender.current) {
            console.log('Filters changed - Gender:', filters.gender, 'Country:', filters.countryId);
            setCurrentPage(1);
            fetchPatientsData(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const handlePageChange = (page, newLimit) => {
        console.log('Pagination changed - Page:', page, 'Limit:', newLimit);
        setCurrentPage(page);

        // If limit changed, update the itemsPerPage state
        if (newLimit && newLimit !== itemsPerPage) {
            setItemsPerPage(newLimit);
        }

        // Fetch data with the new page and limit
        const { from, to } = getDateRange(activeTab);

        if (!from || !to) {
            if (activeTab === 'custom') {
                toast.warning(t('reportsPatients.select_both_dates'));
            }
            return;
        }

        // For custom, validate dates before fetching
        if (activeTab === 'custom') {
            if (!validateDates(startDate, endDate)) {
                return;
            }
        }

        const limit = newLimit || itemsPerPage;
        const params = { from, to, page, limit };
        if (filters.selectedDoctor.length > 0) {
            params.doctorId = filters.selectedDoctor.map((d) => d.doctorId).join(',');
        }
        if (filters.gender.length > 0) {
            params.gender = filters.gender.join(',');
        }
        if (filters.countryId.length > 0) {
            params.countryId = filters.countryId.join(',');
        }

        console.log('API Called - Page:', page, 'From:', from, 'To:', to, 'Limit:', limit, 'DoctorIds:', params.doctorId || 'All', 'Genders:', params.gender || 'All', 'Countries:', params.countryId || 'All');
        dispatch(getPatientsByDate(params));
    };

    const handleDownload = async () => {
        const { from, to } = getDateRange(activeTab);

        if (!from || !to) {
            toast.warning(t('reportsPatients.select_valid_date_range'));
            return;
        }

        // For custom, validate dates before downloading
        if (activeTab === 'custom') {
            if (!validateDates(startDate, endDate)) {
                toast.warning(t('reportsPatients.select_valid_date_range'));
                return;
            }
        }

        try {
            const params = { from, to };
            if (filters.selectedDoctor.length > 0) {
                params.doctorId = filters.selectedDoctor.map((d) => d.doctorId).join(',');
            }
            if (filters.gender.length > 0) {
                params.gender = filters.gender.join(',');
            }
            if (filters.countryId.length > 0) {
                params.countryId = filters.countryId.join(',');
            }

            const resultAction = await dispatch(downloadPatientsExcel(params));

            if (downloadPatientsExcel.fulfilled.match(resultAction)) {
                const blob = resultAction.payload;
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `patients-report-${from}-to-${to}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                toast.success(t('reportsPatients.excel_download_success'));
            } else {
                toast.warning(t('reportsPatients.excel_download_failed'));
            }
        } catch (error) {
            toast.warning(t('reportsPatients.excel_download_error'));
        }
    };

    const indexOfFirstPatient = (currentPage - 1) * itemsPerPage;

    // Show empty data when in custom tab without valid dates
    const shouldShowData = activeTab === 'custom' ? (startDate && endDate) : true;
    const displayPatients = shouldShowData ? patientsByDate : [];

    // Calculate age from DOB
    const calculateAge = (dob) => {
        if (!dob) return '-';
        const birthDate = new Date(dob);
        const today = new Date();
        // Use UTC getters for birthDate to avoid timezone shift on DOB
        let age = today.getFullYear() - birthDate.getUTCFullYear();
        const monthDiff = today.getMonth() - birthDate.getUTCMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getUTCDate())) {
            age--;
        }
        return age;
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // Handle view patient details
    const handleViewPatient = async (patient) => {
        const patientId = patient.patientId || patient.id;

        if (!patientId) {
            toast.warning(t('reportsPatients.patient_id_not_found'));
            return;
        }

        setIsModalOpen(true);
        try {
            await dispatch(getPatientById(patientId)).unwrap();
        } catch (error) {
            console.error('Error fetching patient details:', error);
            toast.warning(t('reportsPatients.failed_load_patient'));
            setIsModalOpen(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white w-full rounded-2xl p-4 sm:p-6 shadow-md"
            data-testid="reports-patients-container"
        >
            {/* Header - Same gradient title as Doctors Report */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4" data-testid="reports-patients-header">
                <h3 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent flex items-center gap-3" data-testid="reports-patients-title">
                    {t('reportsPatients.title')}
                </h3>

                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleDownload}
                    disabled={downloadLoading || displayPatients.length === 0}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    data-testid="reports-patients-download-button"
                >
                    <Download className="w-4 h-4" data-testid="reports-patients-download-icon" />
                    {downloadLoading ? t('reportsPatients.downloading') : t('reportsPatients.download_excel')}
                </motion.button>
            </div>

            {/* Tabs, Doctor Selection & Filter - In Same Row */}
            <div className="flex flex-col gap-4 mb-5" data-testid="reports-patients-tabs-filter-section">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start gap-2">
                    {/* Tabs */}
                    <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 bg-gradient-to-r from-gray-50 to-blue-50/30 w-fit rounded-2xl p-1 shadow border border-gray-200 relative" data-testid="reports-patients-tabs-container">
                        {['today', 'week', 'month', 'custom'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`relative px-3 py-2 text-xs sm:text-sm font-medium rounded-xl transition-all duration-300 cursor-pointer z-10 ${activeTab === tab
                                    ? 'text-white'
                                    : 'text-gray-600 hover:text-[#4ABDBD]'
                                    }`}
                                data-testid={`reports-patients-tab-${tab}`}
                            >
                                {activeTab === tab && (
                                    <motion.div
                                        layoutId="activePatientReportTab"
                                        className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-xl shadow-lg !m-0"
                                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                    />
                                )}
                                <span className="relative z-20" data-testid={`reports-patients-tab-text-${tab}`}>
                                    {t(`reportsPatients.${tab}`)}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Filter Button */}
                    <div className="relative w-full sm:w-auto" data-testid="reports-patients-filter-container">
                        <button
                            ref={filterButtonRef}
                            onClick={() => setIsFilterOpen(!isFilterOpen)}
                            className="group flex items-center justify-center gap-2 w-full sm:w-auto h-full px-3 py-2.5 text-white bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                            aria-label={t('reportsPatients.filter')}
                            aria-expanded={isFilterOpen}
                            data-testid="reports-patients-filter-button"
                        >
                            <SlidersHorizontal className="w-5 h-5" data-testid="reports-patients-filter-icon" />
                            {/* {t('reportsPatients.filter')} */}
                        </button>

                        {isFilterOpen && (
                            <FilterPopover ref={filterRef}>
                                <FilterPanel
                                    onClear={() => {
                                        setTempFilters({ gender: [], countryId: [], selectedDoctor: [] });
                                        setFilters({ gender: [], countryId: [], selectedDoctor: [] });
                                        setDoctorSearch('');
                                        setCurrentPage(1);
                                        setIsFilterOpen(false);
                                    }}
                                    onApply={() => {
                                        setFilters(tempFilters);
                                        setCurrentPage(1);
                                        setIsFilterOpen(false);
                                    }}
                                    clearLabel={t('reportsPatients.filter_clear')}
                                    applyLabel={t('reportsPatients.filter_apply')}
                                >
                                    <FilterSection title={t('reportsPatients.filter_gender')} isActive={tempFilters.gender.length > 0}>
                                        <FilterOptionList
                                            multiple
                                            value={tempFilters.gender}
                                            onChange={(val) => setTempFilters({ ...tempFilters, gender: val })}
                                            options={[
                                                { value: 'male', label: t('reportsPatients.filter_male') },
                                                { value: 'female', label: t('reportsPatients.filter_female') },
                                                { value: 'Not_Communicated', label: t('reportsPatients.filter_Not_Communicated') },
                                                { value: 'other', label: t('reportsPatients.filter_others') },
                                            ]}
                                        />
                                    </FilterSection>

                                    <FilterSection title={t('reportsPatients.filter_country')} isActive={tempFilters.countryId.length > 0}>
                                        <FilterCountryList
                                            multiple
                                            value={tempFilters.countryId}
                                            onChange={(val) => setTempFilters({ ...tempFilters, countryId: val })}
                                            countries={countries}
                                            searchPlaceholder={t('reportsPatients.search_placeholder', 'Search...')}
                                        />
                                    </FilterSection>

                                    <FilterSection title={t('reportsPatients.filter_doctor', 'Doctor')} isActive={tempFilters.selectedDoctor.length > 0}>
                                        <FilterSearchableList
                                            multiple
                                            items={doctorNameList || []}
                                            value={tempFilters.selectedDoctor}
                                            onChange={(val) => setTempFilters({ ...tempFilters, selectedDoctor: val })}
                                            labelKey="name"
                                            sublabelKey="surname"
                                            valueKey="doctorId"
                                            searchPlaceholder={t('reportsPatients.search_doctor', 'Search doctor...')}
                                            noResultsText={t('reportsPatients.no_doctors', 'No doctors found')}
                                            allLabel={t('reportsPatients.all_doctors', 'All Doctors')}
                                            loading={loadingList}
                                            onSearch={(val) => setDoctorSearch(val)}
                                        />
                                    </FilterSection>
                                </FilterPanel>
                            </FilterPopover>
                        )}
                    </div>
                </div>

                {activeTab === 'custom' && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="flex flex-col sm:flex-row gap-2"
                        data-testid="reports-patients-custom-date-section"
                    >
                        <div className="w-full sm:flex-1" data-testid="reports-patients-from-date-field-container">
                            <CustomDatePicker
                                value={startDate}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setStartDate(value);
                                    // Validate dates when start date changes
                                    if (value || endDate) {
                                        validateDates(value, endDate);
                                    } else {
                                        setDateErrors({ startDate: '', endDate: '' });
                                    }
                                }}
                                placeholder={t('reportsPatients.from_date')}
                            />
                            {dateErrors.startDate && (
                                <p className="text-red-600 text-xs mt-1" data-testid="reports-patients-from-date-error">{dateErrors.startDate}</p>
                            )}
                        </div>
                        <div className="w-full sm:flex-1" data-testid="reports-patients-to-date-field-container">
                            <CustomDatePicker
                                value={endDate}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setEndDate(value);
                                    // Validate dates when end date changes
                                    if (value || startDate) {
                                        validateDates(startDate, value);
                                    } else {
                                        setDateErrors({ startDate: '', endDate: '' });
                                    }
                                }}
                                minDate={startDate}
                                maxDate={new Date().toISOString().split('T')[0]}
                                placeholder={t('reportsPatients.to_date')}
                            />
                            {dateErrors.endDate && (
                                <p className="text-red-600 text-xs mt-1" data-testid="reports-patients-to-date-error">{dateErrors.endDate}</p>
                            )}
                        </div>
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                                setStartDate('');
                                setEndDate('');
                                setDateErrors({ startDate: '', endDate: '' });
                                setCurrentPage(1);
                            }}
                            className="flex items-center justify-center gap-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg px-4 py-2 hover:bg-[#4ABDBD]/10 hover:text-[#4ABDBD] transition-all"
                            data-testid="reports-patients-reset-button"
                        >
                            <RotateCcw className="w-4 h-4" data-testid="reports-patients-reset-icon" />
                            {t('reportsPatients.reset')}
                        </motion.button>
                    </motion.div>
                )}
            </div>

            {/* Applied Filters Display */}
            {(filters.gender.length > 0 || filters.countryId.length > 0 || filters.selectedDoctor.length > 0) && (
                <div className="mb-6 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 rounded-xl p-4 border border-gray-200 shadow-sm" data-testid="reports-patients-applied-filters-container">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700 mr-2" data-testid="reports-patients-applied-filters-label">
                            {t('reportsPatients.applied_filters')}:
                        </span>

                        {filters.gender.map((g) => (
                            <div key={`gender-${g}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm" data-testid={`reports-patients-applied-gender-chip-${g}`}>
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('reportsPatients.filter_gender')}:</span> {t(`reportsPatients.filter_${g}`)}
                                </span>
                                <button
                                    onClick={() => setFilters({ ...filters, gender: filters.gender.filter((x) => x !== g) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                    data-testid={`reports-patients-applied-gender-remove-button-${g}`}
                                >
                                    <X className="h-3.5 w-3.5" data-testid={`reports-patients-applied-gender-remove-icon-${g}`} />
                                </button>
                            </div>
                        ))}

                        {filters.countryId.map((cid) => (
                            <div key={`country-${cid}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm" data-testid={`reports-patients-applied-country-chip-${cid}`}>
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('reportsPatients.filter_country')}:</span> <MasterLabel type="masterCountry" id={cid} field="countryName" fallback={countries?.find(c => c.countryId === cid)?.countryName || cid} />
                                </span>
                                <button
                                    onClick={() => setFilters({ ...filters, countryId: filters.countryId.filter((x) => x !== cid) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                    data-testid={`reports-patients-applied-country-remove-button-${cid}`}
                                >
                                    <X className="h-3.5 w-3.5" data-testid={`reports-patients-applied-country-remove-icon-${cid}`} />
                                </button>
                            </div>
                        ))}

                        {filters.selectedDoctor.map((d) => (
                            <div key={`doctor-${d.doctorId}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm" data-testid={`reports-patients-applied-doctor-chip-${d.doctorId}`}>
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('reportsPatients.filter_doctor', 'Doctor')}:</span> {`${d.name || ''} ${d.surname || ''}`.trim()}
                                </span>
                                <button
                                    onClick={() => setFilters({ ...filters, selectedDoctor: filters.selectedDoctor.filter((x) => x.doctorId !== d.doctorId) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                    data-testid={`reports-patients-applied-doctor-remove-button-${d.doctorId}`}
                                >
                                    <X className="h-3.5 w-3.5" data-testid={`reports-patients-applied-doctor-remove-icon-${d.doctorId}`} />
                                </button>
                            </div>
                        ))}

                        <button
                            onClick={() => {
                                setFilters({ gender: [], countryId: [], selectedDoctor: [] });
                                setTempFilters({ gender: [], countryId: [], selectedDoctor: [] });
                                setCurrentPage(1);
                            }}
                            className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
                            data-testid="reports-patients-clear-all-button"
                        >
                            <X className="h-3.5 w-3.5" data-testid="reports-patients-clear-all-icon" />
                            {t('reportsPatients.clear_all')}
                        </button>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="flex flex-col items-center justify-center py-4 pt-12" data-testid="reports-patients-loading-container">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]" data-testid="reports-patients-loading-text">{t('reportsPatients.loading')}</span>
                </div>
            )}

            {/* Desktop Table - EXACT same styling as Doctors Report */}
            {!loading && (
                <div className="hidden sm:block overflow-x-auto rounded-md border border-gray-200" data-testid="reports-patients-table-container">
                    <table className="w-full border-collapse rounded-md overflow-hidden" data-testid="reports-patients-table">
                        <thead data-testid="reports-patients-table-head">
                            <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white" data-testid="reports-patients-table-header-row">
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="reports-patients-header-serial">{t('reportsPatients.serial_no')}</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="reports-patients-header-patient-id">{t('reportsPatients.patient_number', 'Patient no.')}</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="reports-patients-header-name">{t('reportsPatients.name')}</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="reports-patients-header-email">{t('reportsPatients.email')}</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="reports-patients-header-phone">{t('reportsPatients.phone')}</th>
                                <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider" data-testid="reports-patients-header-gender">{t('reportsPatients.gender')}</th>
                                <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider" data-testid="reports-patients-header-created-at">{t('reportsPatients.created_at')}</th>
                                <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider" data-testid="reports-patients-header-action">{t('reportsPatients.action')}</th>
                            </tr>
                        </thead>
                        <tbody data-testid="reports-patients-table-body">
                            {displayPatients.length === 0 ? (
                                <tr data-testid="reports-patients-empty-row">
                                    <td colSpan="8" className="p-8 text-center text-gray-500" data-testid="reports-patients-no-data-text">
                                        {t('reportsPatients.no_data')}
                                    </td>
                                </tr>
                            ) : (
                                displayPatients.map((patient, index) => {
                                    const serialNo = indexOfFirstPatient + index + 1;
                                    const fullName = `${patient.name || ''} ${patient.surname || ''}`.trim();
                                    const formattedCreatedAt = formatDate(patient.createdAt);
                                    return (
                                        <tr
                                            key={patient._id || patient.id || patient.patientId || `patient-${index}`}
                                            className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 group"
                                            data-testid={`reports-patients-row-${index}`}
                                        >
                                            <td className="px-4 py-5 text-xs text-gray-600 group-hover:text-gray-700" data-testid={`reports-patients-serial-${index}`}>{serialNo}</td>
                                            <td className="px-4 py-5 text-xs text-gray-600 group-hover:text-gray-700" data-testid={`reports-patients-patient-id-${index}`}>{hospitalPatientCode(patient) || '-'}</td>
                                            <td className="px-4 py-5 text-xs font-medium text-gray-800 group-hover:text-gray-900" data-testid={`reports-patients-name-${index}`}>{fullName || '-'}</td>
                                            <td className="px-4 py-5 text-xs text-gray-600" data-testid={`reports-patients-email-${index}`}>{patient.email || '-'}</td>
                                            <td className="px-4 py-5 text-xs text-gray-600" data-testid={`reports-patients-phone-${index}`}>{patient.phone || '-'}</td>
                                            <td className="px-4 py-5 text-center text-xs text-gray-600 capitalize" data-testid={`reports-patients-gender-${index}`}>{patient.gender ? t(`common.gender.${patient.gender.toLowerCase()}`) : '-'}</td>
                                            <td className="px-4 py-5 text-center text-xs text-gray-600" data-testid={`reports-patients-created-at-${index}`}>{formattedCreatedAt}</td>
                                            <td className="px-4 py-5 text-center" data-testid={`reports-patients-action-cell-${index}`}>
                                                <button
                                                    onClick={() => handleViewPatient(patient)}
                                                    className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden inline-flex items-center justify-center"
                                                    data-testid={`reports-patients-view-button-${index}`}
                                                >
                                                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                    <Eye className="h-4 w-4 relative z-10" data-testid={`reports-patients-view-icon-${index}`} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Mobile Cards - Same minimal style as Doctors Report */}
            {!loading && (
                <div className="block sm:hidden space-y-4" data-testid="reports-patients-mobile-container">
                    {displayPatients.length === 0 ? (
                        <div className="p-8 text-center text-gray-500" data-testid="reports-patients-mobile-no-data">
                            {t('reportsPatients.no_data')}
                        </div>
                    ) : (
                        displayPatients.map((patient, index) => {
                            const serialNo = indexOfFirstPatient + index + 1;
                            const fullName = `${patient.name || ''} ${patient.surname || ''}`.trim();
                            const formattedCreatedAt = formatDate(patient.createdAt);
                            return (
                                <div key={patient._id || patient.id || patient.patientId || `patient-mobile-${index}`} className="p-4 bg-gray-50 rounded-xl border border-gray-200" data-testid={`reports-patients-mobile-card-${index}`}>
                                    <div className="flex justify-between items-start mb-2">
                                        <span className="text-xs font-bold text-[#4ABDBD]" data-testid={`reports-patients-mobile-serial-${index}`}>{t('reportsPatients.serial_no')}: {serialNo}</span>
                                        <button
                                            onClick={() => handleViewPatient(patient)}
                                            className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                            data-testid={`reports-patients-mobile-view-button-${index}`}
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                            <Eye className="h-4 w-4 relative z-10" data-testid={`reports-patients-mobile-view-icon-${index}`} />
                                        </button>
                                    </div>
                                    <div className="text-sm font-medium mb-2" data-testid={`reports-patients-mobile-name-${index}`}>{fullName || '-'}</div>
                                    {hospitalPatientCode(patient) && (
                                        <div className="text-xs text-gray-600" data-testid={`reports-patients-mobile-patient-id-${index}`}>{t('reportsPatients.patient_number', 'Patient no.')}: {hospitalPatientCode(patient)}</div>
                                    )}
                                    <div className="text-xs text-gray-600" data-testid={`reports-patients-mobile-email-${index}`}>{t('reportsPatients.email_label')}: {patient.email || '-'}</div>
                                    <div className="text-xs text-gray-600" data-testid={`reports-patients-mobile-phone-${index}`}>{t('reportsPatients.phone_label')}: {patient.phone || '-'}</div>
                                    <div className="text-xs text-gray-600 capitalize" data-testid={`reports-patients-mobile-gender-${index}`}>{t('reportsPatients.gender_label')}: {patient.gender ? t(`common.gender.${patient.gender.toLowerCase()}`) : '-'}</div>
                                    <div className="text-xs text-gray-600" data-testid={`reports-patients-mobile-created-at-${index}`}>{t('reportsPatients.created_at_label')}: {formattedCreatedAt}</div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {/* Pagination - Same as Doctors Report */}
            {!loading && (shouldShowData ? totalItems : 0) > 0 && (
                <div className="" data-testid="reports-patients-pagination-container">
                    <Pagination
                        totalItems={shouldShowData ? totalItems : 0}
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        onPageChange={handlePageChange}
                        custombutton={displayPatients.length === 0 ? 'disable' : ''}
                    />
                </div>
            )}

            {/* Patient Profile Modal */}
            <PatientProfileModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                }}
                patient={selectedPatient}
                loading={patientLoading}
            />
        </motion.div>
    );
};

export default ReportsPatients;